import { ArdoqId, ViewIds } from '@ardoq/api-types';
import { IconName } from '@ardoq/icons';
import { DropdownItem, DropdownOptionType } from '@ardoq/dropdown-menu';
import { type SettingsConfig, SettingsType } from '@ardoq/view-settings';
import { handleStoreSetting } from 'viewSettings/settingsHelper';
import {
  PermissionContext,
  permissionsOperations,
} from '@ardoq/access-control';
import { ViewSettings, WorkspaceField } from './types';
import {
  getSelectedFieldSettingsName,
  isByCustomField,
  isByDescendants,
  isByIncomingReferences,
  isByOutgoingReferences,
} from './helpers';
import {
  MAX_LIMIT_VALUES,
  SHOW_ALL_COMPONENTS,
  SizeBasedOn,
} from 'views/treemapConsts';
import { DropdownMenuOption } from './Components/DropdownMenuOption';
import { DropdownMenuSectionTitle } from './Components/DropdownMenuSectionTitle';
import { t, type I18nLocale } from '@ardoq/i18n';

const storeSetting = (newSettings: Record<string, unknown>) => () => {
  handleStoreSetting(ViewIds.TREEMAP, newSettings);
};

const getSizeByFieldOptions = (
  settings: ViewSettings,
  fields: WorkspaceField[],
  fieldsInUse: Set<ArdoqId>,
  permissionContext: PermissionContext
) =>
  fields
    .sort(
      (fieldA, fieldB) =>
        Number(fieldsInUse.has(fieldB.name)) -
        Number(fieldsInUse.has(fieldA.name))
    )
    .map(field => {
      const name = field.name;
      const label = field.label;
      const selectedField = getSelectedFieldSettingsName(name);
      const isActive = Boolean(
        isByCustomField(settings.sizeBasedOn) &&
        settings.sizeBasedOn === selectedField
      );
      const isConfidential =
        !permissionsOperations.canSeeConfidentialFieldByName(
          permissionContext,
          name,
          field.workspaceId
        );
      const isUnused = !fieldsInUse.has(name);

      return {
        content: DropdownMenuOption({
          label,
          isActive,
          isUnused,
          isConfidential,
          onClick:
            !isUnused && !isConfidential
              ? storeSetting({
                  sizeBasedOn: selectedField,
                })
              : undefined,
        }),
        type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
      };
    });

type GetSelectSizeAttributes = (args: {
  settings: ViewSettings;
  fields: WorkspaceField[];
  fieldsInUse: Set<ArdoqId>;
  hasIncomingReferences: boolean;
  hasOutgoingReferences: boolean;
  permissionContext: PermissionContext;
  i18nLocale: I18nLocale;
}) => DropdownItem[];

const getSelectSizeAttributes: GetSelectSizeAttributes = ({
  settings,
  fields,
  fieldsInUse,
  hasIncomingReferences,
  hasOutgoingReferences,
  permissionContext,
  i18nLocale,
}) => {
  const sizeByFieldOptions = getSizeByFieldOptions(
    settings,
    fields,
    fieldsInUse,
    permissionContext
  );

  return [
    ...(sizeByFieldOptions.length
      ? [
          {
            content: DropdownMenuSectionTitle({
              label: t(
                i18nLocale,
                'tabview:treemapView.leftMenu.sizeByField',
                'Size by field'
              ),
            }),
            type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
          },
          ...sizeByFieldOptions,
          { type: DropdownOptionType.DIVIDER },
        ]
      : []),
    {
      content: DropdownMenuSectionTitle({
        label: t(
          i18nLocale,
          'tabview:treemapView.leftMenu.sizeByDescendantCount',
          'Size by descendant count'
        ),
      }),
      type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
    },
    {
      content: DropdownMenuOption({
        label: t(
          i18nLocale,
          'tabview:treemapView.leftMenu.descendants',
          'Descendants'
        ),
        isActive: isByDescendants(settings),
        onClick: storeSetting({ sizeBasedOn: SizeBasedOn.CHILD_COUNT }),
      }),
      type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
    },
    { type: DropdownOptionType.DIVIDER },

    {
      content: DropdownMenuSectionTitle({
        label: t(
          i18nLocale,
          'tabview:treemapView.leftMenu.sizeByReferenceCount',
          'Size by reference count'
        ),
      }),
      type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
    },
    {
      content: DropdownMenuOption({
        label: t(
          i18nLocale,
          'tabview:treemapView.leftMenu.incomingReference',
          'Incoming reference'
        ),
        isActive: isByIncomingReferences(settings),
        isUnused: !hasIncomingReferences,
        onClick: storeSetting({
          sizeBasedOn: SizeBasedOn.INCOMING_LINKS,
        }),
      }),
      type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
    },
    {
      content: DropdownMenuOption({
        label: t(
          i18nLocale,
          'tabview:treemapView.leftMenu.outgoingReference',
          'Outgoing reference'
        ),
        isActive: isByOutgoingReferences(settings),
        isUnused: !hasOutgoingReferences,
        onClick: storeSetting({
          sizeBasedOn: SizeBasedOn.OUTGOING_LINKS,
        }),
      }),
      type: DropdownOptionType.CUSTOM_CONTENT_OPTION,
    },
  ];
};

const getMaxLimitOptions = (settings: ViewSettings, i18nLocale: I18nLocale) => [
  {
    name: 'all',
    label: t(i18nLocale, 'tabview:treemapView.leftMenu.all', 'All'),
    isActive: settings.maxLimitValue === SHOW_ALL_COMPONENTS,
    onClick: storeSetting({ maxLimitValue: SHOW_ALL_COMPONENTS }),
    type: DropdownOptionType.OPTION,
  },
  ...MAX_LIMIT_VALUES.map(limitValue => ({
    name: String(limitValue),
    label: String(limitValue),
    isActive: settings.maxLimitValue === limitValue,
    onClick: storeSetting({ maxLimitValue: limitValue }),
    type: DropdownOptionType.OPTION,
  })),
];

type GetLeftMenuArgs = {
  settings: ViewSettings;
  fields: WorkspaceField[];
  fieldsInUse: Set<ArdoqId>;
  hasIncomingReferences: boolean;
  hasOutgoingReferences: boolean;
  permissionContext: PermissionContext;
  i18nLocale: I18nLocale;
};

export const getLeftMenu = ({
  settings,
  fields,
  fieldsInUse,
  hasIncomingReferences,
  hasOutgoingReferences,
  permissionContext,
  i18nLocale,
}: GetLeftMenuArgs): SettingsConfig[] => {
  const { maxLimitValue } = settings;
  const selectedSizeAttributes = getSelectSizeAttributes({
    settings,
    fields,
    fieldsInUse,
    hasIncomingReferences,
    hasOutgoingReferences,
    permissionContext,
    i18nLocale,
  });
  const isMaxFilterOn =
    !!maxLimitValue && maxLimitValue !== SHOW_ALL_COMPONENTS;
  return [
    {
      id: 'selectedSizeAttribute',
      label: t(
        i18nLocale,
        'tabview:treemapView.leftMenu.selectSizeAttribute',
        'Select size attribute'
      ),
      options: selectedSizeAttributes,
      type: SettingsType.DROPDOWN,
      iconName: IconName.FORMAT_LIST_BULLETED,
    },
    {
      id: 'maxLimit',
      label: !isMaxFilterOn
        ? t(
            i18nLocale,
            'tabview:treemapView.leftMenu.showingAllComponents',
            'Showing all components'
          )
        : t(
            i18nLocale,
            'tabview:treemapView.leftMenu.showingTopN',
            { count: maxLimitValue },
            'Showing top {{count}}'
          ),
      options: getMaxLimitOptions(settings, i18nLocale),
      type: SettingsType.DROPDOWN,
      iconName: IconName.LEVEL_DOWN,
      isActive: isMaxFilterOn,
    },
  ];
};
